<template>
  <main class="main">
    <div class="section breadcrumbs-section">
      <div class="wrapper"></div>
    </div>
    <div class="section static-section pay-section">
      <div class="wrapper">
        <ValidationObserver v-slot="{ invalid, handleSubmit }" ref="card_form">
          <form @submit.prevent="handleSubmit(onCardSubmit)">
            <div class="form-fields-wrapper">
              <div class="cart-up">
                <div class="topup-form">
                  <!-- Card Number -->

                  <label>
                    <div class="desc">{{ $t("Email") }}:</div>
                    <input
                      type="email"
                      :placeholder="$t('Email')"
                      v-model="email"
                    />
                  </label>
                  <label>
                    <div class="desc">{{ $t("Nickname") }}:</div>
                    <input
                      type="text"
                      :placeholder="$t('Nickname')"
                      v-model="nickname"
                    />
                  </label>
                  <label>
                    <div class="desc">{{ $t("Phone (optional)") }}:</div>
                    <input
                      type="number"
                      :placeholder="$t('Phone')"
                      v-model="phone"
                    />
                  </label>
                  <label class="label input-container">
                    <div class="desc">{{ $t("Card number") }}</div>
                    <ValidationProvider name="formattedPan" v-slot="{ errors }">
                      <input
                        type="text"
                        :placeholder="$t('Card number')"
                        :value="formattedPan"
                        @input="onCardNumberInput"
                      />
                      <span v-if="errors[0]" class="error">{{
                        $t(errors[0])
                      }}</span>
                    </ValidationProvider>
                  </label>

                  <!-- Expiration Date -->
                  <label class="label input-container">
                    <div class="desc">{{ $t("Expiration Date") }} (MM/YY)</div>
                    <ValidationProvider
                      name="expiration_date"
                      v-slot="{ errors }"
                    >
                      <input
                        type="text"
                        :placeholder="$t('MM/YY')"
                        :value="expiration_date"
                        @input="onExpirationInput"
                      />
                      <span v-if="errors[0]" class="error">{{
                        $t(errors[0])
                      }}</span>
                    </ValidationProvider>
                  </label>

                  <!-- Card Holder Name -->
                  <label class="label input-container">
                    <div class="desc">{{ $t("Card Holder Name") }}</div>
                    <ValidationProvider
                      name="card_holdername"
                      v-slot="{ errors }"
                    >
                      <input
                        type="text"
                        :placeholder="$t('Card Holder Name')"
                        :value="card_holdername"
                        @input="onCardHolderInput"
                      />
                      <span v-if="errors[0]" class="error">{{
                        $t(errors[0])
                      }}</span>
                    </ValidationProvider>
                  </label>

                  <!-- CVV -->
                  <label class="label input-container">
                    <div class="desc">{{ $t("CVV") }}</div>
                    <ValidationProvider name="card_cvv" v-slot="{ errors }">
                      <input
                        type="password"
                        :placeholder="$t('CVV')"
                        :value="card_cvv"
                        @input="onCvvInput"
                      />
                      <span v-if="errors[0]" class="error">{{
                        $t(errors[0])
                      }}</span>
                    </ValidationProvider>
                  </label>
                </div>
              </div>

              <div class="cart-bottom">
                <button
                  type="submit"
                  :disabled="invalid"
                  class="button w-132 blue"
                >
                  <span v-if="!loaded">{{ $t("Pay") }}</span>
                  <span v-if="loaded" class="loader"></span>
                </button>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </main>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      formattedPan: "",
      expiration_date: "",
      card_holdername: "",
      card_cvv: "",
      loaded: false,
      email: "",
      phone: "",
      nickname: "",
    };
  },
  mounted() {
    this.$http
      .get(process.env.VUE_APP_API + "user/profile")
      .then((res) => {
        this.email = res.data.email;
        this.card_holdername =
          res.data.name.toUpperCase() + " " + res.data.name.toUpperCase();
        this.phone = res.data.phone;
        this.nickname = res.data.nickname;
      })
      .catch((res) => {
        if (res.response.status === 401) {
          this.$parent.openSignIn();
        }
      });
  },
  methods: {
    // =======================
    // Format/Sanitize Helpers
    // =======================
    formatCardCvv(value) {
      value = value.replace(/\D/g, "");
      return value.slice(0, 3);
    },
    formatCardNumber(value) {
      value = value.replace(/\D/g, "");
      if (value.length > 16) value = value.slice(0, 16);
      return value.replace(/(\d{4})(?=\d)/g, "$1 ");
    },
    formatExpirationDate(value) {
      value = value.replace(/\D/g, "");
      if (value.length > 4) value = value.slice(0, 4);
      if (value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2);
      }
      const [month] = value.split("/");
      if (month && parseInt(month, 10) > 12) {
        value = "12" + value.slice(2);
      }
      return value;
    },
    formatCardHolderName(value) {
      value = value.replace(/[^A-Za-z\s]/g, "").replace(/\s+/g, " ");
      const words = value.trim().split(" ");
      if (words.length > 2) value = words.slice(0, 2).join(" ");
      return value.toUpperCase();
    },

    // Input Handlers
    onCardNumberInput(e) {
      const masked = this.formatCardNumber(e.target.value);
      this.formattedPan = masked;
    },
    onExpirationInput(e) {
      this.expiration_date = this.formatExpirationDate(e.target.value);
    },
    onCardHolderInput(e) {
      this.card_holdername = this.formatCardHolderName(e.target.value);
    },
    onCvvInput(e) {
      const masked = this.formatCardCvv(e.target.value);
      this.card_cvv = masked;
    },

    // Submit Handler
    async onCardSubmit() {
      try {
        const [expiry_month, expiry_year] = this.expiration_date.split("/");

        const formData = {
          card_number: this.formattedPan.replace(/\s+/g, ""),
          expiry_month,
          expiry_year,
          cardholder_name: this.card_holdername,
          cvv: this.card_cvv,
          email: this.email,
          phone: this.phone,
          nickname: this.nickname,
        };

        const combinedData = formData;

        this.loaded = true;
        const res = await this.$http.post(
          process.env.VUE_APP_API + `orders/checkout/execute`,
          combinedData,
        );

        if (res.data.status == "ERROR" && res.data.message) {
          this.loaded = false;
          this.$refs.card_form.setErrors({
            card_cvv: res.data.message,
          });
        } else {
          this.loaded = false;
          window.location = res.data.redirect_url;
        }
      } catch (error) {
        this.loaded = false;

        if (error.response) {
          if (error.response.data.errors) {
            this.$refs.card_form.setErrors(error.response.data.errors);
          } else if (
            error.response.data.status === "ERROR" &&
            error.response.data.message
          ) {
            this.$refs.card_form.setErrors({
              card_cvv: error.response.data.message,
            });
          } else {
            this.$refs.card_form.setErrors({
              card_cvv: error.response.data.message,
            });
          }
        }
      }
    },
  },
};
</script>
